import useFormDetailsPage from '@/hooks/use-form-details-page';
import type { CopyNode } from '@/types/copy';
import { RATE_LIMIT_TYPES } from '@/types/error';
import { ArrowUpRight, Info, Sparkle } from 'lucide-react';

import React, { useCallback, useEffect, useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { RateLimitData } from '@/features/focus-mode/types/chat-section';

import { COPY } from '@/shared/constants/copies';
import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';

import { cn } from '@/lib/utils';

import { APP_ROUTES } from '../constants/navigation';
import { useAppStore } from '../contexts/app-wrapper/providers/app-provider';
import { useCurrentPlan } from '../hooks/use-current-plan';
import { getUpgradeDestination } from '../utils/pricing';
import { openUrlInNewTab } from '../utils/window';
import { CopyText } from './copy';
import { ShowUsageButton } from './show-usage-button';

/** Context for rate limit - helps differentiate what action triggered the limit */
export type LimitContext = 'ai' | 'linkedin_preview' | 'send_message' | 'stage_change';

/**
 * Which registry entry answers each limit type. AI and email draw on the same
 * monthly credits, so they share one message; interviews, assignments, stage
 * changes and offer letters are count-based plan limits with their own. The referrer variant is a
 * sibling key rather than a branch.
 */
const LIMIT_COPY_KEYS: Record<
	RATE_LIMIT_TYPES,
	'exhausted' | 'interviews' | 'assignments' | 'stageChanges' | 'offers'
> = {
	[RATE_LIMIT_TYPES.AI_CREDITS]: 'exhausted',
	[RATE_LIMIT_TYPES.REPLIES]: 'exhausted',
	[RATE_LIMIT_TYPES.INTERVIEWS]: 'interviews',
	[RATE_LIMIT_TYPES.ASSIGNMENTS]: 'assignments',
	[RATE_LIMIT_TYPES.STAGE_CHANGES]: 'stageChanges',
	[RATE_LIMIT_TYPES.OFFERS]: 'offers',
};

/** Only these draw on monthly credits, so only these can be topped up from Billing. */
const CREDIT_LIMIT_TYPES = new Set([RATE_LIMIT_TYPES.AI_CREDITS, RATE_LIMIT_TYPES.REPLIES]);

export function getLimitCopy(limitType: RATE_LIMIT_TYPES, isReferrer: boolean) {
	// `limit_type` comes straight off the wire — a type the backend adds before the frontend
	// knows it gets the credits message instead of an undefined lookup that crashes the render.
	const key = LIMIT_COPY_KEYS[limitType] ?? 'exhausted';
	return isReferrer ? COPY.limits[`${key}Referrer`] : COPY.limits[key];
}

export function useLimitUpgrade({
	limitType,
	source,
	pageId,
}: {
	limitType: RATE_LIMIT_TYPES;
	source: string;
	pageId?: string | null;
}) {
	const currentUserRole = useAppStore((state) => state.selectedWorkspace?.currentUserRole);
	const { isFreePlan } = useCurrentPlan();

	const handleUpgradeClick = useCallback(() => {
		emitMixpanelEvent(mixpanelEvents.CLICKED_UPGRADE, {
			feature: MIXPANEL_APP_FEATURES.FORM,
			source,
			user_role: currentUserRole === 'ADMIN' ? 'admin' : 'member',
			button_location: limitType,
		});
		openUrlInNewTab(
			getUpgradeDestination({
				isCreditsNudge: CREDIT_LIMIT_TYPES.has(limitType),
				isFreePlan,
				pageId,
			})
		);
	}, [source, pageId, currentUserRole, limitType, isFreePlan]);

	return { isReferrer: currentUserRole === 'REFERRER', handleUpgradeClick };
}

export const RateLimitWarningNudge = ({
	rateLimitData,
	source,
	pageId,
	className,
	overrideCopy,
	limitContext,
}: {
	rateLimitData: RateLimitData | null;
	source?: string;
	pageId?: string;
	className?: string;
	overrideCopy?: { title: string; description: CopyNode };
	limitContext?: LimitContext;
}) => {
	const { updatedFormDetails: formDetails } = useFormDetailsPage();

	const limitType = rateLimitData?.limit_type || RATE_LIMIT_TYPES.REPLIES;
	const netPageId = pageId || formDetails?.pageId;
	const { isReferrer, handleUpgradeClick } = useLimitUpgrade({
		limitType,
		source: source || 'subscription_nudge',
		pageId: netPageId,
	});

	const copy = useMemo(() => {
		// Referrer copy always takes priority over any custom override.
		if (isReferrer) {
			return getLimitCopy(limitType, true);
		}

		return overrideCopy ?? getLimitCopy(limitType, false);
	}, [limitType, overrideCopy, isReferrer]);

	useEffect(() => {
		if (rateLimitData) {
			emitMixpanelEvent(mixpanelEvents.VIEWED_RATE_LIMIT_BANNER, {
				feature: MIXPANEL_APP_FEATURES.FORM,
				source,
				limit_type: rateLimitData.limit_type,
				limit_context: limitContext,
			});
		}
	}, [rateLimitData?.limit_type, source, limitContext]);

	if (!rateLimitData) {
		return null;
	}

	return (
		<div className={cn('rounded-md border border-amber-200 bg-amber-50 p-4', className)}>
			<div className="flex justify-between gap-2">
				<div>
					<h3 className="text-[15px] font-semibold text-amber-800">{copy.title}</h3>
					<p className="mt-1 text-[13px] text-amber-700">
						<CopyText
							value={copy.description}
							actions={{ upgrade: handleUpgradeClick }}
						/>
						{!isReferrer && CREDIT_LIMIT_TYPES.has(limitType) && (
							<>
								{' '}
								<ShowUsageButton
									source={source || 'subscription_nudge'}
									pageId={netPageId}
									variant="link"
								/>
							</>
						)}
					</p>
				</div>
				{!isReferrer && (
					<Button
						variant="ghost"
						size="sm"
						className="flex-shrink-0 gap-1 bg-amber-800 text-xs text-white hover:bg-amber-900 hover:text-white"
						onClick={handleUpgradeClick}
					>
						{COPY.plans.upgradeCta}
						<ArrowUpRight size={16} />
					</Button>
				)}
			</div>
		</div>
	);
};

export const LinkedInRateLimitNudge = React.memo(
	({
		rateLimitData,
		pageId,
		className,
	}: {
		rateLimitData: RateLimitData | null;
		pageId?: string;
		className?: string;
	}) => {
		const selectedWorkspace = useAppStore((state) => state.selectedWorkspace);
		const { updatedFormDetails: formDetails } = useFormDetailsPage();

		const currentUserRole = selectedWorkspace?.currentUserRole;
		const { isFreePlan } = useCurrentPlan();
		const isReferrer = currentUserRole === 'REFERRER';
		const netPageId = pageId || formDetails?.pageId;

		useEffect(() => {
			if (rateLimitData) {
				emitMixpanelEvent(mixpanelEvents.VIEWED_RATE_LIMIT_BANNER, {
					feature: MIXPANEL_APP_FEATURES.FORM,
					source: 'linkedin_preview',
					limit_type: rateLimitData.limit_type,
					limit_context: 'linkedin_preview' as LimitContext,
				});
			}
		}, [rateLimitData?.limit_type]);

		const handleUpgradeClick = useCallback(() => {
			emitMixpanelEvent(mixpanelEvents.CLICKED_UPGRADE, {
				feature: MIXPANEL_APP_FEATURES.FORM,
				source: 'linkedin_preview',
				user_role: currentUserRole === 'ADMIN' ? 'admin' : 'member',
				button_location: 'PREVIEWS',
			});
			// LinkedIn lookups spend the shared monthly credits.
			openUrlInNewTab(
				getUpgradeDestination({ isCreditsNudge: true, isFreePlan, pageId: netPageId })
			);
		}, [netPageId, currentUserRole, isFreePlan]);

		if (!rateLimitData) {
			return null;
		}

		return (
			<div
				className={cn(
					'flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2',
					className
				)}
			>
				<Sparkle className="h-3 w-3 flex-shrink-0 text-amber-800" />
				<CopyText
					value={isReferrer ? COPY.limits.linkedInLookupReferrer : COPY.limits.linkedInLookup}
					actions={{ upgrade: handleUpgradeClick }}
					className="text-[13px] text-amber-800"
				/>
				<ShowUsageButton
					source="linkedin_preview"
					pageId={netPageId}
					variant="link"
					className="text-[13px] text-amber-800"
				/>
				<Tooltip>
					<TooltipTrigger asChild>
						<Info className="h-3.5 w-3.5 flex-shrink-0 text-amber-800" />
					</TooltipTrigger>
					<TooltipContent
						side="top"
						className="max-w-[300px]"
					>
						{COPY.limits.linkedInLookupTip}
					</TooltipContent>
				</Tooltip>
			</div>
		);
	}
);

LinkedInRateLimitNudge.displayName = 'LinkedInRateLimitNudge';

/**
 * Generic plan-gate nudge for features that require a higher-tier plan.
 * Use this whenever a feature is locked behind a plan and you need to show
 * a banner with a description and a solid upgrade CTA.
 */
export const PlanGateNudge = React.memo(
	({
		description,
		referrerDescription,
		onUpgrade,
		secondaryAction,
		className,
	}: {
		description: string;
		referrerDescription?: string;
		onUpgrade?: () => void;
		secondaryAction?: React.ReactNode;
		className?: string;
	}) => {
		const selectedWorkspace = useAppStore((state) => state.selectedWorkspace);
		const isReferrer = selectedWorkspace?.currentUserRole === 'REFERRER';

		const handleUpgrade = useCallback(() => {
			if (onUpgrade) {
				onUpgrade();
			} else {
				// A plan gate is never solved by extra credits — it needs a different plan.
				openUrlInNewTab(APP_ROUTES.PLANS);
			}
		}, [onUpgrade]);

		const displayDescription = isReferrer
			? (referrerDescription ?? COPY.plans.referrerUpgradeFallback)
			: description;

		return (
			<div className={cn('rounded-md border border-amber-200 bg-amber-50 p-2', className)}>
				<div className="flex items-center justify-between gap-2">
					<p className="text-sm font-medium text-amber-700">
						{displayDescription}
						{!isReferrer && secondaryAction ? <> {secondaryAction}</> : null}
					</p>
					{!isReferrer && (
						<Button
							variant="ghost"
							size="sm"
							className="flex-shrink-0 gap-1 bg-amber-800 text-xs text-white hover:bg-amber-900 hover:text-white"
							onClick={handleUpgrade}
						>
							{COPY.plans.upgradeCta}
							<ArrowUpRight size={16} />
						</Button>
					)}
				</div>
			</div>
		);
	}
);

PlanGateNudge.displayName = 'PlanGateNudge';
